/**
 * Column definitions for the Event Manager table, split out of the page for the
 * same reason `roster-columns.tsx` is: the page owns behaviour, this owns
 * presentation. Sortable columns use the `sort_by` values the events service
 * whitelists (`EVENT_SORT_FIELDS`), so a header click is a server-side sort.
 */
import { Link } from 'react-router-dom';

import type { DataTableColumn } from '../../components/data-table';
import { StatusPill, type StatusTone } from '../../components/status-pill';
import type { TeamEvent } from '../../lib/api-types';
import { APP_VIEWS } from '../../lib/constants';
import {
  CLOSED_EVENT_TOOLTIP,
  EVENT_STATUS_LABELS,
  EVENT_TYPE_LABELS,
  FILM_REVIEW_LABEL,
  formatEventDateTime,
} from '../../lib/event-format';

const STATUS_TONE: Record<TeamEvent['status'], StatusTone> = {
  pending: 'neutral',
  active: 'success',
  // FR-EVENTS-07: film-review mode is a state, not a failure — never error red.
  closed: 'warning',
};

export function EventStatusBadge({ event }: { event: TeamEvent }): JSX.Element {
  const isClosed = event.status === 'closed';
  return (
    <StatusPill
      tone={STATUS_TONE[event.status]}
      title={isClosed ? CLOSED_EVENT_TOOLTIP : undefined}
    >
      {isClosed ? FILM_REVIEW_LABEL : EVENT_STATUS_LABELS[event.status]}
    </StatusPill>
  );
}

export function buildEventColumns(
  tenantSlug: string,
  activeEventId: string | null = null
): readonly DataTableColumn<TeamEvent>[] {
  return [
    {
      id: 'name',
      header: 'Event',
      isSortable: true,
      isPinned: true,
      minWidth: 180,
      maxWidth: 360,
      grow: 3,
      render: (event) => (
        <span className="event-cell">
          <Link
            className="event-cell__name"
            to={`/app/${tenantSlug}/${APP_VIEWS.events}/${event.event_id}`}
            title={event.name}
          >
            {event.name}
          </Link>
          {event.event_id === activeEventId ? (
            <StatusPill tone="brand" title="The event you are working on">
              Current
            </StatusPill>
          ) : null}
        </span>
      ),
      renderDetail: (event) => event.name,
    },
    {
      id: 'scheduled_at',
      header: 'Date & time',
      isSortable: true,
      priority: 1,
      minWidth: 170,
      maxWidth: 210,
      grow: 0,
      render: (event) => formatEventDateTime(event.scheduled_at),
    },
    {
      id: 'opponent',
      header: 'Opponent',
      priority: 3,
      minWidth: 130,
      grow: 2,
      render: (event) => event.opponent_name ?? '—',
    },
    {
      id: 'event_type',
      header: 'Type',
      isSortable: true,
      priority: 4,
      minWidth: 90,
      maxWidth: 120,
      grow: 0,
      render: (event) => EVENT_TYPE_LABELS[event.event_type],
    },
    {
      id: 'season_year',
      header: 'Season',
      isSortable: true,
      priority: 6,
      align: 'end',
      minWidth: 100,
      maxWidth: 100,
      grow: 0,
      render: (event) => String(event.season_year),
    },
    {
      id: 'location',
      header: 'Location',
      priority: 5,
      minWidth: 130,
      grow: 2,
      render: (event) => event.location ?? '—',
    },
    {
      id: 'status',
      header: 'Status',
      isSortable: true,
      priority: 2,
      // Fits the widest pill, "Film Review Mode" (~134px), plus cell padding.
      minWidth: 160,
      maxWidth: 160,
      grow: 0,
      render: (event) => <EventStatusBadge event={event} />,
    },
  ];
}
